import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback
} from 'react';
import { useTranslation } from 'react-i18next';
import { H5PPlayerUI, H5PEditorUI } from '@lumieducation/h5p-react';

import { useAuth } from '@hooks/useAuth';

import { Button } from '@components/shares';
import { FormPromptAI } from './FormPromptAI';
import { Col, Flex, Row, Spin } from 'antd/es';
import Link from 'antd/es/typography/Link';
import { getDownloadH5PUrl } from '@utils/H5PUtil';

type H5PEditorProps = {
  className?: string;
  contentId?: string;
  userId?: string;
  loading?: boolean;
  view?: 'edit' | 'play' | 'create';
  canEdit?: boolean;
};

export enum H5PState {
  edit = 'edit',
  play = 'play',
  create = 'create',
}

type H5PViewState = {
  state: string;
};

const H5PServerUrl = process.env.REACT_H5P_SERVER_API_URL;

export const H5PEditor = forwardRef(function H5PEditorComponent(
  props: H5PEditorProps,
  ref: any,
) {
  const { t } = useTranslation();
  const { auth } = useAuth();

  const h5pEditorRef = useRef(null);
  const h5pPlayer = useRef(null);
  const refFormPromptAI = useRef(null);

  const [contextId, setContextId] = useState<string>(undefined);
  const [asUserId, setAsUserId] = useState<string>(undefined);
  const [readOnlyState, setReadOnlyState] = useState<boolean>(false);
  const [h5pState, setH5PState] = useState<H5PViewState>({
    state: props.view || H5PState.edit,
  });
  const [h5pContent, setH5PContent] = useState({
    contentId: props.contentId || 'new',
    metadata: {},
  });
  const [isAutoH5P, setAutoH5P] = useState<boolean>(false);

  useImperativeHandle(
    ref,
    () => ({
      setH5PView(mode: H5PState) {
        setH5PState({
          state: mode,
        });
      },
      async save() {
        return await handleSaveH5P();
      },
    }),
    [],
  );

  const onSaveH5P = async (
    contentId: string,
    requestBody: { library: string; params: any },
  ): Promise<{ contentId: string; metadata: IContentMetadata }> => {
    console.log('callback ContentService save');
    if (contentId) {
      console.log(`ContentService: Saving new content.`);
    } else {
      console.log(`ContentService: Saving content ${contentId}`);
    }

    const body = JSON.stringify({
      ...requestBody,
      user: { id: 'anonymous' },
    });

    console.log(body)

    const res = contentId
      ? await fetch(`${H5PServerUrl}/h5p/${contentId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'CSRF-Token': '',
        },
        body,
      })
      : await fetch(H5PServerUrl + '/h5p', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'CSRF-Token': '',
        },
        body,
      });

    if (!res || !res.ok) {
      throw new Error(`${res.status} ${res.statusText} - ${await res.text()}`);
    }

    setH5PContent({
      contentId,
      metadata: requestBody,
    });

    return res.json();
  };

  const onSaveError = async (event: any) => {
    console.log(event);
  };

  const onSaved = async (event: any) => {
    console.log(event);
  };

  const onEditorLoaded = () => {
    // this.setState({ loading: false });
  };

  const getEdit = async (contentId: string): Promise<any> => {
    const query = new URLSearchParams();
    if (auth) {
      query.append('user_id', `${'anonymous'}`);
    }
    const queryString = query.toString();
    const res = await fetch(
      `${H5PServerUrl}/h5p/${contentId}/edit${queryString ? `?${queryString}` : ''
      }`,
    );
    // const res = await fetch(`${this.baseUrl}/${contentId}/edit`);
    if (!res || !res.ok) {
      throw new Error(`${res.status} ${res.statusText}`);
    }
    return res.json();
  };

  const getPlay = async (
    contentId: string,
    contextId?: string,
    asUserId?: string,
    readOnlyState?: boolean,
  ): Promise<any> => {
    console.log(
      `ContentService: Getting information to play ${contentId}${contextId ? `, contextId ${contextId}` : ''
      }${asUserId ? `, asUserId ${asUserId}` : ''}${readOnlyState !== undefined ? `, readOnlyState ${readOnlyState}` : ''
      }...`,
    );

    const query = new URLSearchParams();
    if (contextId) {
      query.append('contextId', contextId);
    }
    if (asUserId) {
      query.append('asUserId', asUserId);
    }
    if (readOnlyState === true) {
      query.append('readOnlyState', 'yes');
    }
    if (auth) {
      query.append('user_id', `${'anonymous'}`);
    }

    const queryString = query.toString();

    const res = await fetch(
      `${H5PServerUrl}/h5p/${h5pContent.contentId}/play${queryString ? `?${queryString}` : ''
      }`,
    );
    if (!res || !res.ok) {
      throw new Error(`${res.status} ${res.statusText}`);
    }
    return res.json();
  };

  const handleChangeAutoH5PView = useCallback(() => {
    setAutoH5P((isAuto) => {
      isAuto = !isAuto;
      if (isAuto) {
        setH5PState({
          state: H5PState.play,
        });
      } else {
        setH5PState({
          state: H5PState.edit,
        });
      }

      return isAuto;
    });
  }, [h5pState]);

  const handleSaveH5P = useCallback(async () => {
    try {
      const h5pResponse = await h5pEditorRef.current?.save({});

      if (h5pResponse) {
        setH5PContent(h5pResponse);

        if (h5pResponse) {
          setH5PState({
            state: H5PState.edit,
          });
          setH5PContent({
            contentId: h5pResponse.contentId,
            metadata: {},
          });
        }
        return h5pResponse;
      } else {
        return null;
      }
    } catch (_error) {
      return null;
    }
  }, []);

  const handleChangeView = useCallback(async () => {
    if (H5PState.edit && h5pContent.contentId) {
      await handleSaveH5P();
    }

    setH5PState(({ state }) => {
      return {
        state:
          state === H5PState.edit && h5pContent.contentId
            ? H5PState.play
            : H5PState.edit,
      };
    });
  }, [h5pContent.contentId]);

  const handleCreateJobGenByAI = useCallback(async () => {
    refFormPromptAI.current.submit();
  }, []);

  return (
    <div className={props.className || ''}>
      {props.canEdit && (
        <Row style={{
          marginBottom: 10,
        }}>
          <Col xs={24} sm={16}>
            <Flex gap={10}>
              <Button variant='transparent' onClick={handleChangeAutoH5PView}>
                {t(`material.h5p.${isAutoH5P ? 'manual' : 'auto'}`)}
              </Button>
              {!isAutoH5P && h5pContent.contentId === 'new' && (
                <Button onClick={handleSaveH5P}>{t('material.create')}</Button>
              )}
              {h5pContent.contentId &&
                h5pContent.contentId !== 'new' &&
                !isAutoH5P && (
                  <Button onClick={handleChangeView}>
                    {h5pState.state === H5PState.play
                      ? t('material.edit')
                      : t('material.preview')}
                  </Button>
                )}
              {isAutoH5P && (
                <Button onClick={handleCreateJobGenByAI}>
                  {t('material.h5p.regenerate')}
                </Button>
              )}
            </Flex>
          </Col>
          <Col xs={24}  sm={8}>
            <Flex justify='end'>
              {h5pContent.contentId && (
                <Link
                  href={getDownloadH5PUrl(h5pContent.contentId)}
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {t('material.download')}
                </Link>
              )}
            </Flex>
          </Col>
        </Row>
      )}
      <div className='flex flex-col sm:flex-row'>
        {isAutoH5P && (
          <div className='w-full sm:w-[33%] pr-4'>
            <FormPromptAI ref={refFormPromptAI} />
          </div>
        )}

        <div className={`w-full loading`}>
          <Spin spinning={props.loading}>
            {h5pContent.contentId &&
              (h5pState.state === H5PState.edit ||
                h5pState.state === H5PState.create) && (
                <H5PEditorUI
                  ref={h5pEditorRef}
                  contentId={h5pContent.contentId}
                  loadContentCallback={getEdit}
                  saveContentCallback={onSaveH5P}
                  onSaved={onSaved}
                  onLoaded={onEditorLoaded}
                  onSaveError={onSaveError}
                />
              )}
            {(h5pState.state === H5PState.play || isAutoH5P) &&
              h5pContent.contentId && (
                <H5PPlayerUI
                  ref={h5pPlayer}
                  contentId={h5pContent.contentId}
                  contextId={contextId}
                  asUserId={asUserId}
                  readOnlyState={readOnlyState}
                  loadContentCallback={getPlay}
                />
              )}
          </Spin>
        </div>
      </div>
    </div>
  );
});

export interface IContentChange {
  author?: string;
  date?: string;
  log?: string;
}

export interface IContentAuthor {
  name?: string;
  role?: string;
}

export interface ILicenseData {
  defaultLanguage: string;
  a11yTitle?: string;
  license: string;
  licenseVersion?: string;
  yearFrom?: string;
  yearTo?: string;
  source?: string;
  title: string;
  authors?: IContentAuthor[];
  licenseExtras?: string;
  changes?: IContentChange[];
  authorComments?: string;
  contentType?: string;
}

export interface ILibraryName {
  machineName: string;
  majorVersion: number;
  minorVersion: number;
}

export interface IContentMetadata extends ILicenseData {
  dynamicDependencies?: ILibraryName[];
  editorDependencies?: ILibraryName[];
  embedTypes: ('iframe' | 'div')[];
  h?: string;
  language: string;
  mainLibrary: string;
  metaDescription?: string;
  metaKeywords?: string;
  /**
   * A flat list of all dependencies required for the content to be displayed.
   * Note that this list is a flat representation of the full dependency tree
   * that is created by looking up all dependencies of the main library.
   */
  preloadedDependencies: ILibraryName[];
  w?: string;
}
